<template>
  <div class="undoRedoContainer tw-z-[2] tw-ml-5">
    <ConfirmModal
      ref="resetModal"
      :title="$t('resetDesignTitle')"
      :descriptions="[$t('resetDesignDescription'), $t('resetDesignConfirm')]"
      :primary-button-label="$t('resetDesign')"
      :secondary-button-label="$t('cancel')"
      @onConfirm="reset"
    />

    <div class="tw-flex tw-gap-4 tw-items-center tw-cursor-pointer">
      <!-- Restart Button with Text -->
      <div class="tw-flex tw-items-center tw-gap-2" @click="$refs.resetModal.open()">
        <img
            :src="getAssetPath('/assets/images/reset.svg')"
            width="24"
        />
        <span class="tw-text-white tw-font-semibold tw-text-sm">{{ $t('restart') }}</span>
      </div>

      <!-- Undo Button -->
      <button
          class="undo-redo-btn"
          @click="undo"
          :class="{
          'tw-opacity-30': !currentIndex,
        }"
      >
        <img
            :src="getAssetPath('/assets/images/undo.svg')"
            width="20"
        />
      </button>

      <!-- Redo Button -->
      <button
          class="undo-redo-btn"
          @click="redo"
          :class="{
          'tw-opacity-30': currentIndex === states.length - 1,
        }"
      >
        <img
            :src="getAssetPath('/assets/images/redo.svg')"
            width="20"
        />
      </button>
    </div>
  </div>
</template>

<script>
import updateAllObjectsCrossOrigin from "@/utils/helpers/updateAllObjectsCrossOrigin";
import controlsVisibility from "@/constants/controlsVisibility";
import { nextTick } from "vue";
import { getAssetPath } from "@/utils/assetPath";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import { trackEvent } from "@/utils/tracking";

export default {
  components: {
    ConfirmModal,
  },
  props: {
    isFlipped: {
      type: Boolean,
      required: true,
    },
    /** Default canvas background color when reset (e.g. from cardConfig) */
    defaultBackgroundColor: {
      type: String,
      default: "#fff",
    },
  },
  setup() {
    return {
      getAssetPath
    };
  },
  data() {
    return {
      states: [],
      currentIndex: 0,
      blockAddingNewState: false,
      initialState: [],
    };
  },
  mounted() {
    this.debouncedSetEventListeners();
  },
  methods: {
    hasStates() {
      return !!(this.states.length - 1)
    },
    debouncedSetEventListeners() {
      setTimeout(() => {
        this.setEventListeners(true);
      }, 2000);
    },
    setEventListeners(initialState) {
      if ((this.isFlipped && !window.backCanvas) || (!this.isFlipped && !window.frontCanvas)) {
        this.debouncedSetEventListeners();
        return;
      }

      if (this.isFlipped) {
        window.backCanvas.on({
          "object:added": this.addState,
          "object:removed": this.addState,
          "object:modified": this.addState,
        });
      } else {
        window.frontCanvas.on({
          "object:added": this.addState,
          "object:removed": this.addState,
          "object:modified": this.addState,
        });
      }
      this.pushNewState(initialState);
    },
    async reset() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Restart",
      });
      this.states = JSON.parse(JSON.stringify(this.initialState));
      this.currentIndex = 0;

      await this.loadState();

      const activeCanvas = this.isFlipped ? window.backCanvas : window.frontCanvas;
      if (!activeCanvas) return;

      // Reset background (Fabric toJSON/loadFromJSON does not always restore backgroundColor)
      const defaultBg = this.defaultBackgroundColor || "#fff";
      activeCanvas.set("backgroundColor", defaultBg);
      activeCanvas.backgroundImage = null;
      activeCanvas.requestRenderAll();

      // Apply bounding-box fixes for text objects
      activeCanvas.getObjects().forEach(obj => {
        if (obj.type && obj.type.toLowerCase().includes("text")) {
          obj.set({
            objectCaching: false,
            top: Math.max(obj.top, 0),
            lineHeight: 1.1
          });
          obj._clearCache();
          obj.initDimensions();
          obj.setCoords();
        }
      });

      activeCanvas.renderAll();
      this.$emit('updateState', this.hasStates());
    },

    async resetState() {
      if (this.isFlipped) {
        window.backCanvas.off({
          "object:added": this.addState,
          "object:removed": this.addState,
          "object:modified": this.addState,
        });
      } else {
        window.frontCanvas.off({
          "object:added": this.addState,
          "object:removed": this.addState,
          "object:modified": this.addState,
        });
      }

      this.states = [];
      this.currentIndex = 0;
      this.blockAddingNewState = false;

      await nextTick();

      this.initialState = [];
      this.setEventListeners(true);

      this.$emit('updateState', this.hasStates())
    },
    pushNewState(initialState) {
      let canvasState;
      if (this.isFlipped) {
        canvasState = window.backCanvas.toJSON([ "id", "selectable" ]);
      } else {
        canvasState = window.frontCanvas.toJSON([ "id", "selectable" ]);
      }

      this.states.push(canvasState);

      if (initialState) {
        if (this.initialState.length === 0) {
          this.initialState.push(JSON.parse(JSON.stringify(canvasState)));
        }
      }

      this.$emit('updateState', this.hasStates())
    },
    addState() {
      if (this.blockAddingNewState) return;

      this.states.splice(this.currentIndex + 1);
      this.pushNewState();
      this.currentIndex++;

      this.$emit('updateState', this.hasStates())
    },
    undo() {
      if (this.currentIndex === 0) {
        return;
      }
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Undo",
      });
      this.currentIndex--;
      this.loadState();
    },
    redo() {
      if (this.currentIndex === this.states.length - 1) {
        return;
      }
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Redo",
      });
      this.currentIndex++;
      this.loadState();
    },
    async loadState() {
      this.blockAddingNewState = true;
      const currentState = this.states[this.currentIndex];
      updateAllObjectsCrossOrigin(currentState.objects);

      if (this.isFlipped) {
        await window.backCanvas.loadFromJSON(currentState);
        this.normalizeObjects();
        window.backCanvas.renderAll();
      } else {
        await window.frontCanvas.loadFromJSON(currentState);
        this.normalizeObjects();
        window.frontCanvas.renderAll();
      }

      this.blockAddingNewState = false;
      this.$emit('updateState', this.hasStates())
    },
    normalizeObjects() {
      if (this.isFlipped) {
        window.backCanvas.getObjects().forEach((obj) => {
          obj.setControlsVisibility(controlsVisibility);
        });
      } else {
        window.frontCanvas.getObjects().forEach((obj) => {
          obj.setControlsVisibility(controlsVisibility);
        });
      }
    },
  },
};
</script>
